// P9.2 — Capability-pack registry. Named trading concepts are OPTIONAL PLUGINS above the
// methodology-neutral core: a pack defines concepts that compile down to generic events,
// regions, and relations. The core engine never depends on pack vocabulary; a stage that
// requires an unloaded pack concept raises UnsupportedCapabilityError (a counted failure,
// never a silent guess and never a silent pass).
//
// Purity: registry state is module-scoped and explicit; packs register deterministic
// feed constructors only (no I/O, no clock, no randomness).

import type { Bar } from '../structuralPrimitives';
import type { EventFeed } from '../setupStateMachine';

export interface ConceptDefinition {
  /** Human definition — objective, versioned, falsifiable. */
  describe: string;
  version: number;
  /** Build a deterministic EventFeed for this concept over the trigger-TF bars.
   *  `direction` is the setup's trade direction; params are the stage's frozen params.
   *  Feeds receive GuardContext (consumed events) so concepts may reference earlier
   *  stages' outputs — the temporal chain stays real. */
  makeFeed(bars: readonly Bar[], params: Record<string, number | string | boolean>, direction: 'long' | 'short'): EventFeed;
}

export interface CapabilityPack {
  id: string;
  version: number;
  concepts: Record<string, ConceptDefinition>;
}

export class UnsupportedCapabilityError extends Error {
  constructor(public readonly concept: string) {
    super(`unsupported capability: '${concept}' — required pack is not loaded (counted as an unsupported-capability failure; never silently substituted)`);
    this.name = 'UnsupportedCapabilityError';
  }
}

const packs = new Map<string, CapabilityPack>();

export function registerPack(p: CapabilityPack): void { packs.set(p.id, p); }
export function unregisterPack(id: string): void { packs.delete(id); }
export function loadedPacks(): string[] { return [...packs.keys()].sort(); }

/** Resolve '<packId>:<concept>' → definition, or throw UnsupportedCapabilityError. */
export function resolveConcept(qualified: string): ConceptDefinition {
  const i = qualified.indexOf(':');
  if (i <= 0) throw new UnsupportedCapabilityError(qualified);
  const pack = packs.get(qualified.slice(0, i));
  const def = pack?.concepts[qualified.slice(i + 1)];
  if (!def) throw new UnsupportedCapabilityError(qualified);
  return def;
}
export function conceptAvailable(qualified: string): boolean {
  try { resolveConcept(qualified); return true; } catch { return false; }
}
